import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHeader } from '../../context/HeaderContext';
import { getActiveCampaigns, getPastCampaigns, getCampaignImpactSummary, getAllCampaignImpacts } from '../../api/campaign';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { FaFileAlt, FaLeaf, FaGift, FaAd, FaTree, FaCalendarAlt, FaBlog } from 'react-icons/fa';

const Dashboard = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const { partnerType } = useSelector((state) => state.partner);

  const [loading, setLoading] = useState(true);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [pastCampaigns, setPastCampaigns] = useState([]);
  const [impactSummary, setImpactSummary] = useState(null);
  const [campaignImpacts, setCampaignImpacts] = useState([]);

  const isNGO = partnerType === 'ngo';

  useEffect(() => {
    setHeaderTitle('Dashboard');
    fetchDashboardData();
  }, [setHeaderTitle]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [activeCampaignsData, pastCampaignsData, impactSummaryData, campaignImpactsData] = await Promise.all([
        getActiveCampaigns().catch(() => []),
        getPastCampaigns().catch(() => []),
        getCampaignImpactSummary().catch(() => null),
        getAllCampaignImpacts().catch(() => []),
      ]);

      setActiveCampaigns(Array.isArray(activeCampaignsData) ? activeCampaignsData : []);
      setPastCampaigns(Array.isArray(pastCampaignsData) ? pastCampaignsData : []);
      setImpactSummary(impactSummaryData);
      setCampaignImpacts(Array.isArray(campaignImpactsData) ? campaignImpactsData : []);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (campaign) => {
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    const now = new Date();

    if (now < start) return 0;
    if (now > end) return 100;

    const total = end - start;
    const elapsed = now - start;
    return Math.round((elapsed / total) * 100);
  };

  const formatDate = (dateString, withYear = false) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (withYear) {
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const getCampaignTypeIcon = (type) => {
    const campaignType = (type || '').toLowerCase();
    let icon = <FaLeaf className='text-orange-500' />;

    if (campaignType.includes('challenge')) {
      icon = <FaLeaf className='text-green-500' />;
    } else if (campaignType.includes('brand') || campaignType.includes('product')) {
      icon = <FaGift className='text-blue-500' />;
    } else if (campaignType.includes('advertisement')) {
      icon = <FaAd className='text-purple-500' />;
    } else if (campaignType.includes('tree') || campaignType.includes('plant')) {
      icon = <FaTree className='text-green-600' />;
    } else if (campaignType.includes('event')) {
      icon = <FaCalendarAlt className='text-red-500' />;
    } else if (campaignType.includes('blog')) {
      icon = <FaBlog className='text-indigo-500' />;
    }

    return (
      <div className='w-10 h-10 bg-gray-50 rounded-md flex items-center justify-center border border-border text-lg'>
        {icon}
      </div>
    );
  };

  const getCampaignTypeBadge = (type) => {
    const campaignType = (type || 'Sponsor a Challenge');
    let color = 'text-orange-500';

    if (campaignType.toLowerCase().includes('brand') || campaignType.toLowerCase().includes('product')) {
      color = 'text-blue-500';
    } else if (campaignType.toLowerCase().includes('advertisement')) {
      color = 'text-purple-500';
    } else if (campaignType.toLowerCase().includes('tree') || campaignType.toLowerCase().includes('plant')) {
      color = 'text-green-600';
    }

    return (
      <span className={`text-xs ${color} font-medium`}>
        {campaignType}
      </span>
    );
  };

  // Get impact metrics based on partner type
  const getImpactMetrics = () => {
    // totalImpactMetrics is nested inside the summary response
    const metrics = impactSummary?.totalImpactMetrics || {};
    const allMetrics = [
      {
        label: 'Carbon Savings (kg)',
        value: metrics.totalCarbonSavings || 0,
        valueColor: 'text-chipGreen',
        bg: 'bg-[#e6f9ec]',
        border: 'border-[#d4eed7]',
        showFor: 'all',
      },
      {
        label: 'Points Redeemed',
        value: metrics.totalPointsRedeemed || 0,
        valueColor: 'text-chipPurple',
        bg: 'bg-[#f4e7ff]',
        border: 'border-[#e8dffb]',
        showFor: 'all',
      },
      {
        label: 'Users Engaged',
        value: metrics.totalUsersEngaged || 0,
        valueColor: 'text-chipBlue',
        bg: 'bg-[#e7f1ff]',
        border: 'border-[#d5e3ff]',
        showFor: 'all',
      },
      {
        label: 'Trees Planted',
        value: metrics.totalTreesPlanted || 0,
        valueColor: 'text-green-600',
        bg: 'bg-[#e6f9ec]',
        border: 'border-[#d4eed7]',
        showFor: 'all',
      },
      {
        label: 'Certificates Issued',
        value: metrics.totalCertificatesIssued || 0,
        valueColor: 'text-chipBlue',
        bg: 'bg-[#e7f1ff]',
        border: 'border-[#d5e3ff]',
        showFor: 'all',
      },
      {
        label: 'Volunteers',
        value: metrics.totalVolunteers || 0,
        valueColor: 'text-chipPurple',
        bg: 'bg-[#f4e7ff]',
        border: 'border-[#e8dffb]',
        showFor: 'ngo',
      },
      {
        label: 'Meals Distributed',
        value: metrics.totalMealsDistributed || 0,
        valueColor: 'text-orange-500',
        bg: 'bg-[#fff5e6]',
        border: 'border-[#ffe4c2]',
        showFor: 'ngo',
      },
      {
        label: 'CTR',
        value: `${metrics.totalClicks && metrics.totalImpressions ? ((metrics.totalClicks / metrics.totalImpressions) * 100).toFixed(1) : 0}%`,
        valueColor: 'text-red-500',
        bg: 'bg-[#ffeef0]',
        border: 'border-[#fdd9de]',
        showFor: 'all',
      },
      {
        label: 'Impressions',
        value: metrics.totalImpressions || 0,
        valueColor: 'text-red-500',
        bg: 'bg-[#ffeef0]',
        border: 'border-[#fdd9de]',
        showFor: 'all',
      },
      {
        label: 'Coupons Redeemed',
        value: metrics.totalCouponsRedeemed || 0,
        valueColor: 'text-chipYellow',
        bg: 'bg-[#fff5d9]',
        border: 'border-[#ffeec2]',
        showFor: 'company', // Hide for NGO
      },
      {
        label: 'Submissions',
        value: metrics.totalSubmissions || 0,
        valueColor: 'text-chipPurple',
        bg: 'bg-[#f4e7ff]',
        border: 'border-[#e8dffb]',
        showFor: 'company', // Hide for NGO
      },
    ];

    // Filter metrics based on partner type
    return allMetrics.filter(metric => {
      if (metric.showFor === 'all') return true;
      if (metric.showFor === 'ngo' && isNGO) return true;
      if (metric.showFor === 'company' && !isNGO) return true;
      return false;
    });
  };

  // Render campaign-type specific metrics for individual cards
  const renderCampaignMetrics = (item) => {
    const campaignType = (item.campaign?.campaignType || '').toLowerCase();
    const impact = item.impactSummary || {};

    // Brand/Product Promotion
    if (campaignType.includes('brand') || campaignType.includes('product')) {
      return (
        <>
          {/* Product/Brand Name */}
          {item.campaign?.productName && (
            <div className='flex items-center justify-between'>
              <span className='text-sm text-secondary'>Product/Brand</span>
              <span className='text-base font-semibold text-fontBlack'>
                {item.campaign.productName}
              </span>
            </div>
          )}

          {/* Coupon Section */}
          <div className='mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200'>
            <h5 className='text-sm font-semibold text-fontBlack mb-2'>Coupons</h5>
            <div className='grid grid-cols-2 gap-2'>
              <div className='text-center'>
                <p className='text-lg font-bold text-fontBlack'>{impact.totalCoupons || 0}</p>
                <p className='text-xs text-secondary'>Total</p>
              </div>
              <div className='text-center'>
                <p className='text-lg font-bold text-chipGreen'>{impact.availableCoupons || 0}</p>
                <p className='text-xs text-secondary'>Available</p>
              </div>
              <div className='text-center'>
                <p className='text-lg font-bold text-chipBlue'>{impact.assignedCoupons || 0}</p>
                <p className='text-xs text-secondary'>Assigned</p>
              </div>
              <div className='text-center'>
                <p className='text-lg font-bold text-chipYellow'>{impact.redeemedCoupons || 0}</p>
                <p className='text-xs text-secondary'>Redeemed</p>
              </div>
            </div>
          </div>

          {/* KPI Section */}
          <div className='mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200'>
            <h5 className='text-sm font-semibold text-fontBlack mb-2'>KPIs</h5>
            <div className='space-y-1'>
              <div className='flex justify-between'>
                <span className='text-xs text-secondary'>Impressions</span>
                <span className='text-sm font-semibold'>{impact.totalImpressions || 0}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-xs text-secondary'>Revenue</span>
                <span className='text-sm font-semibold'>₹{impact.totalRevenueGenerated || 'NA'}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-xs text-secondary'>Conversion Rate</span>
                <span className='text-sm font-semibold'>{impact.conversionRate || 'NA'}%</span>
              </div>
            </div>
          </div>

          <div className='flex items-center justify-between mt-3'>
            <span className='text-sm text-secondary'>Campaign Budget</span>
            <span className='text-base font-semibold text-chipBlue'>
              ₹{item.campaign?.budget || item.campaign?.campaignBudget || 0}
            </span>
          </div>
        </>
      );
    }

    // Sponsor Challenge
    if (campaignType.includes('challenge')) {
      return (
        <>
          {/* Coupon Section */}
          <div className='p-3 bg-yellow-50 rounded-lg border border-yellow-200'>
            <h5 className='text-sm font-semibold text-fontBlack mb-2'>Coupons</h5>
            <div className='grid grid-cols-2 gap-2'>
              <div className='text-center'>
                <p className='text-lg font-bold text-fontBlack'>{impact.totalCoupons || impact.campaignCoupons?.length || 0}</p>
                <p className='text-xs text-secondary'>Total</p>
              </div>
              <div className='text-center'>
                <p className='text-lg font-bold text-chipGreen'>{impact.availableCoupons || 0}</p>
                <p className='text-xs text-secondary'>Available</p>
              </div>
              <div className='text-center'>
                <p className='text-lg font-bold text-chipBlue'>{impact.assignedCoupons || 0}</p>
                <p className='text-xs text-secondary'>Assigned</p>
              </div>
              <div className='text-center'>
                <p className='text-lg font-bold text-chipYellow'>{impact.redeemedCoupons || 0}</p>
                <p className='text-xs text-secondary'>Redeemed</p>
              </div>
            </div>
          </div>

          {/* KPI Section */}
          <div className='mt-3 p-3 bg-green-50 rounded-lg border border-green-200'>
            <h5 className='text-sm font-semibold text-fontBlack mb-2'>KPIs</h5>
            <div className='space-y-1'>
              <div className='flex justify-between'>
                <span className='text-xs text-secondary'>Carbon Savings</span>
                <span className='text-sm font-semibold text-chipGreen'>{impact.totalCarbonSavingsKg || 0} kg</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-xs text-secondary'>Total Submissions</span>
                <span className='text-sm font-semibold'>{impact.totalSubmissions || 0}</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-xs text-secondary'>Challenge Points</span>
                <span className='text-sm font-semibold'>{impact.totalChallengePoints || 0}</span>
              </div>
            </div>
          </div>

          <div className='flex items-center justify-between mt-3'>
            <span className='text-sm text-secondary'>Campaign Budget</span>
            <span className='text-base font-semibold text-chipBlue'>
              ₹{item.campaign?.budget || item.campaign?.campaignBudget || 0}
            </span>
          </div>
        </>
      );
    }

    // Plant Trees
    if (campaignType.includes('tree') || campaignType.includes('plant')) {
      return (
        <>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>Trees Planted</span>
            <span className='text-base font-semibold text-green-600'>
              {impact.totalTreesPlanted || 0}
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>Certificates Issued</span>
            <span className='text-base font-semibold text-chipBlue'>
              {impact.totalCertificatesIssued || 0}
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>Carbon Savings</span>
            <span className='text-base font-semibold text-chipGreen'>
              {impact.totalCarbonSavingsKg || 0} kg
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>Points Donated</span>
            <span className='text-base font-semibold text-chipPurple'>
              {impact.totalPointsDonated || 0}
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>Campaign Budget</span>
            <span className='text-base font-semibold text-chipBlue'>
              ₹{item.campaign?.budget || item.campaign?.campaignBudget || 0}
            </span>
          </div>
        </>
      );
    }

    // Advertisement
    if (campaignType.includes('advertisement') || campaignType.includes('ad')) {
      return (
        <>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>Total Impressions</span>
            <span className='text-base font-semibold text-fontBlack'>
              {impact.totalImpressions || 0}
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>Unique Impressions</span>
            <span className='text-base font-semibold text-chipPurple'>
              {impact.uniqueImpressions || 0}
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>Total Clicks</span>
            <span className='text-base font-semibold text-chipBlue'>
              {impact.totalClicks || 0}
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>CTR</span>
            <span className='text-base font-semibold text-red-500'>
              {impact.clickThroughRate || 0}%
            </span>
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-sm text-secondary'>Campaign Budget</span>
            <span className='text-base font-semibold text-chipBlue'>
              ₹{item.campaign?.budget || item.campaign?.campaignBudget || 0}
            </span>
          </div>
        </>
      );
    }

    // Default fallback
    return (
      <>
        <div className='flex items-center justify-between'>
          <span className='text-sm text-secondary'>Carbon Savings</span>
          <span className='text-base font-semibold text-chipGreen'>
            {impact.totalCarbonSavingsKg || 0} kg
          </span>
        </div>
        <div className='flex items-center justify-between'>
          <span className='text-sm text-secondary'>Total Submissions</span>
          <span className='text-base font-semibold text-chipPurple'>
            {impact.totalSubmissions || 0}
          </span>
        </div>
        <div className='flex items-center justify-between'>
          <span className='text-sm text-secondary'>Challenge Points</span>
          <span className='text-base font-semibold text-chipPurple'>
            {impact.totalChallengePoints || 0}
          </span>
        </div>
        <div className='flex items-center justify-between'>
          <span className='text-sm text-secondary'>Campaign Budget</span>
          <span className='text-base font-semibold text-chipBlue'>
            ₹{item.campaign?.budget || item.campaign?.campaignBudget || 0}
          </span>
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  const totalCampaigns = activeCampaigns.length + pastCampaigns.length;
  const activeRate = totalCampaigns > 0 ? ((activeCampaigns.length / totalCampaigns) * 100).toFixed(0) : 0;
  const impactMetrics = getImpactMetrics();

  return (
    <div className='w-full'>
      {/* Active and Past Campaigns Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6 mb-6'>
        {/* Active Campaigns */}
        <div className='bg-white rounded-lg p-8 shadow-md'>
          <h3 className='text-xl font-semibold text-fontBlack mb-4'>Active Campaigns</h3>
          <div className='space-y-3'>
            {activeCampaigns.length > 0 ? (
              activeCampaigns.map((campaign, index) => {
                const progress = calculateProgress(campaign);

                const progressLabel = progress >= 100
                  ? 'Completed'
                  : progress >= 75
                    ? 'Almost done'
                    : progress >= 50
                      ? 'Half way'
                      : 'Just started';

                return (
                  <div
                    key={campaign._id || campaign.id || index}
                    className='border-b pb-5 last:border-b-0 last:pb-0'
                    style={{ borderColor: 'rgba(224,230,237,0.5)' }}
                  >
                    <div className='flex items-center gap-4'>
                      {getCampaignTypeIcon(campaign.campaignType)}
                      <div className='flex-1'>
                        <h4 className='font-medium text-sm truncate text-fontBlack'>{campaign.title || campaign.campaignTitle || 'Untitled Campaign'}</h4>
                        <div className='flex items-center gap-2 mt-1'>
                          <div className='flex-1 bg-[#e0e6ea] rounded-full h-2'>
                            <div
                              className='h-2 rounded-full transition-all duration-300'
                              style={{
                                width: `${progress}%`,
                                background: 'linear-gradient(90deg, #7de672 0%, #34c759 65%, #09b94b 100%)',
                              }}
                            ></div>
                          </div>
                          <span className='text-sm text-[#4b5563]'>{progress}%</span>
                        </div>
                        <div className='flex items-center gap-2 mt-1'>
                          <span className='text-xs text-[#6b7280]'>{campaign.campaignType || progressLabel}</span>
                          <span className='text-xs text-[#c4c7cd]'>•</span>
                          <span className='text-xs text-[#6b7280]'>₹{((campaign.campaignBudget ?? campaign.budget) || 0)}</span>
                        </div>
                      </div>
                      <div className='text-right min-w-[90px]'>
                        <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#ecf8ef] text-[#168c41] border border-[#19a44c]'>
                          Active
                        </span>
                        <p className='text-xs text-[#6b7280] mt-1'>Ends {formatDate(campaign.endDate, true)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className='text-center py-12 text-secondary'>
                <p className='text-base'>No active campaigns</p>
                <button
                  onClick={() => navigate('/campaigns/new-req')}
                  className='mt-4 text-primary hover:underline font-medium'
                >
                  Create your first campaign
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Past Campaigns */}
        <div className='bg-white rounded-lg p-8 shadow-sm'>
          <h3 className='text-lg font-semibold text-fontBlack mb-4'>Past Campaigns</h3>
          <div className='space-y-3'>
            {pastCampaigns.length > 0 ? (
              <div className='text-center py-12'>
                <p className='text-2xl font-bold text-fontBlack'>{pastCampaigns.length}</p>
                <p className='text-sm text-secondary mt-2'>Total Past Campaigns</p>
                <button
                  onClick={() => navigate('/campaigns/past')}
                  className='mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryHoverDark transition-colors font-semibold'
                >
                  View All Past Campaigns
                </button>
              </div>
            ) : (
              <div className='flex flex-col items-center justify-center py-12 text-secondary'>
                <div className='text-chipYellow text-6xl mb-4'>
                  <FaFileAlt />
                </div>
                <p className='text-base font-medium text-fontBlack'>No past campaigns found</p>
                <p className='text-sm text-secondary mt-2'>Past campaigns will appear here once completed</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Impact Summary Section */}
      <div className='bg-white rounded-[20px] p-8 shadow-sm border border-[#ebeff5] mb-6 mx-auto'>
        <h3 className='text-lg font-semibold text-fontBlack mb-6'>
          Impact Summary (All Campaigns) {isNGO && <span className='text-sm text-secondary font-normal'>- NGO View</span>}
        </h3>

        {/* Campaign Overview */}
        <div className='mb-6'>
          <h4 className='text-base font-semibold text-fontBlack mb-4'>Campaign Overview</h4>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            {[
              {
                value: totalCampaigns,
                label: 'Total Campaigns',
                bg: 'bg-white',
                border: 'border-[#eef0f6]',
                valueColor: 'text-fontBlack',
              },
              {
                value: activeCampaigns.length,
                label: 'Active',
                bg: 'bg-[#ecf8ef]',
                border: 'border-[#dbedd6]',
                valueColor: 'text-chipGreen',
              },
              {
                value: pastCampaigns.length,
                label: 'Inactive',
                bg: 'bg-[#fff3f3]',
                border: 'border-[#f5dcdd]',
                valueColor: 'text-red-500',
              },
              {
                value: `${activeRate}%`,
                label: 'Active Rate',
                bg: 'bg-[#e5f1ff]',
                border: 'border-[#d3e2ff]',
                valueColor: 'text-chipBlue',
              },
            ].map((card) => (
              <div
                key={card.label}
                className={`${card.bg} ${card.border} border rounded-[18px] p-4 shadow-sm text-center`}
              >
                <p className={`text-2xl font-bold ${card.valueColor}`}>
                  {card.value}
                </p>
                <p className='text-sm text-secondary mt-1'>{card.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Campaign Types */}
        <div className='mb-6'>
          <h4 className='text-base font-semibold text-fontBlack mb-4'>Campaign Types</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className='flex items-center gap-3 bg-gray-50 rounded-lg p-4'>
              {getCampaignTypeIcon('challenge')}
              <div>
                <p className='text-2xl font-bold text-fontBlack'>
                  {activeCampaigns.filter(c => (c.campaignType || '').toLowerCase().includes('challenge')).length}
                </p>
                <p className='text-sm text-secondary'>Sponsor Challenge</p>
              </div>
            </div>
            <div className='flex items-center gap-3 bg-gray-50 rounded-lg p-4'>
              {getCampaignTypeIcon('brand')}
              <div>
                <p className='text-2xl font-bold text-fontBlack'>
                  {activeCampaigns.filter(c => (c.campaignType || '').toLowerCase().includes('brand') || (c.campaignType || '').toLowerCase().includes('product')).length}
                </p>
                <p className='text-sm text-secondary'>Brand/Product</p>
              </div>
            </div>
            <div className='flex items-center gap-3 bg-gray-50 rounded-lg p-4'>
              {getCampaignTypeIcon('tree')}
              <div>
                <p className='text-2xl font-bold text-fontBlack'>
                  {activeCampaigns.filter(c => (c.campaignType || '').toLowerCase().includes('tree') || (c.campaignType || '').toLowerCase().includes('plant')).length}
                </p>
                <p className='text-sm text-secondary'>Plant Trees</p>
              </div>
            </div>
            <div className='flex items-center gap-3 bg-gray-50 rounded-lg p-4'>
              {getCampaignTypeIcon('advertisement')}
              <div>
                <p className='text-2xl font-bold text-fontBlack'>
                  {activeCampaigns.filter(c => (c.campaignType || '').toLowerCase().includes('advertisement')).length}
                </p>
                <p className='text-sm text-secondary'>Advertisement</p>
              </div>
            </div>
          </div>
        </div>

        {/* Impact Metrics - Filtered by partner type */}
        <div>
          <h4 className='text-base font-semibold text-fontBlack mb-4'>Impact Metrics</h4>
          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3'>
            {impactMetrics.map((metric) => (
              <div
                key={metric.label}
                className={`${metric.bg} ${metric.border} border rounded-[16px] px-3 py-4 text-center shadow-sm`}
              >
                <p className={`text-2xl font-semibold ${metric.valueColor}`}>
                  {metric.value}
                </p>
                <p className='text-xs text-secondary mt-2'>{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Individual Campaign Impact Details */}
      <div className='bg-white rounded-lg p-8 shadow-md'>
        <h3 className='text-lg font-semibold text-fontBlack mb-6'>Individual Campaign Impact Details</h3>
        {campaignImpacts.length > 0 ? (
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {campaignImpacts.map((item, index) => (
              <div key={index} className='border border-border rounded-lg p-6 bg-gray-50 hover:shadow-lg transition-shadow relative'>
                {/* Status Badge */}
                <div className='absolute top-4 right-4'>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    item.campaign?.active
                      ? 'bg-[#ecf8ef] text-[#168c41] border border-[#19a44c]'
                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                  }`}>
                    {item.campaign?.active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Campaign Header */}
                <div className='flex items-start gap-3 mb-4'>
                  {getCampaignTypeIcon(item.campaign?.campaignType)}
                  <div className='flex-1'>
                    <h4 className='font-semibold text-fontBlack text-base'>
                      {item.campaign?.title || item.campaign?.campaignTitle || 'Untitled Campaign'}
                    </h4>
                    {getCampaignTypeBadge(item.campaign?.campaignType)}
                  </div>
                </div>

                {/* Date Range */}
                <p className='text-sm text-secondary mb-4'>
                  {formatDate(item.campaign?.startDate)} - {formatDate(item.campaign?.endDate)}
                </p>

                {/* Campaign Type Specific Metrics */}
                <div className='space-y-3'>
                  {renderCampaignMetrics(item)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className='text-center py-12 text-secondary'>
            <p className='text-base'>No campaign impact data available yet</p>
          </div>
        )}
      </div>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default Dashboard;
